<template>
    <v-container>
        <v-btn icon x-small
               color="primary" @click="isReplaceModalVisible = !isReplaceModalVisible">
            <v-icon class="text-right">mdi-pencil-box-multiple</v-icon>
        </v-btn>

        <v-btn x-small icon color="primary" v-if="chartType === 'WORDCLOUD'"
               @click="onOpenWordcloudCaption">
            <v-icon class="text-right">mdi-help-circle</v-icon>
        </v-btn>

        <v-btn x-small color="primary" icon v-if="chartType === 'LINE'"
               @click="onOpenLinechartCaption">
            <v-icon class="text-right">mdi-help-circle</v-icon>
        </v-btn>

        <v-btn x-small color="primary" icon v-if="chartType === 'TABLE'"
               @click="onOpenTablechartCaption">
            <v-icon class="text-right">mdi-help-circle</v-icon>
        </v-btn>

        <v-skeleton-loader v-show="isChartLoading && chartId"
                           type="image"></v-skeleton-loader>


        <div v-show="!chartId" class="w-100 text-center mt-6 mb-6">Add a chart in order to view.</div>

        <!-- <div v-show="!isChartLoading && (!chartResult.data || chartResult.data.length === 0)" class="subtitle-1 w-100 text-center mt-6"> No results found, increase your data range or change the filters</div>-->

        <word-cloud-chart v-if="chartType === 'WORDCLOUD' && !isChartLoading"
                          :data="chartResult.data"
                          :rotation="chartResult.rotation"
                          :rotationThreshold="chartResult.rotationThreshold"
                          :minFontSize="chartResult.minFontSize"
                          :maxFontSize="chartResult.maxFontSize"
                          :accuracy="chartResult.accuracy"
                          :step="chartResult.step"
                          :colors="chartResult.colors"
                          :report-filter-override="reportFilterOverride"
                          :title="chartResult.title"></word-cloud-chart>


        <pie-chart v-if="chartType === 'PIE' && !isChartLoading"
                   :data="chartResult.data"
                   :title="chartResult.title"
                   :type="chartResult.type"
                   :colors="chartResult.colors"
                   :show-date-range="showDateRange"
                   :opacity="chartResult.opacity"></pie-chart>

        <line-chart v-if="chartType === 'LINE' && !isChartLoading"
                    :data="chartResult.data"
                    :title="chartResult.title"
                    :type="chartResult.chartType"
                    :opacity="chartResult.opacity"
                    :stack="chartResult.stack"
                    :legend="chartResult.legend"
                    :bullet="chartResult.bullet"></line-chart>

        <table-chart v-if="chartType === 'TABLE' && !isChartLoading"
                     :headers="chartHeaders"
                     @on-operator-select="onOperatorSelect"
                     @on-severity-select="onSeveritySelect"
                     :table-filter="tableFilter"
                     :is-loading="isChartLoading"
                     :data="chartResult.data"></table-chart>

        <chart-replace-modal :report-layout-id="reportLayoutId"
                             @close="onClose"
                             @replaced="onReplaced"
                             :is-visible="isReplaceModalVisible"
                             :is-report-builder="isReportBuilder"></chart-replace-modal>

    </v-container>
</template>

<script>
    import LineChart from "../../components/charts/LineChart"
    import TableChart from "../../components/charts/TableChart"
    import ChartReplaceModal from "../../components/charts/ChartReplaceModal"
    import WordCloudChart from "../../components/charts/WordCloudChart"
    import PieChart from "../../components/charts/PieChart"
    import reportService from "../../services/reportService"
    import ReportTableService from "../../services/reportTableService"
    import moment from 'moment';
    import { EventBus } from '../../event/event-bus.js';

    export default {
        name: 'ChartSelector',
        data: function () {
            return {
                isChartLoading: true,
                chartResult: {},
                isReplaceModalVisible: false,
                chartHeaders: [],
                chartPageNo: 1,
                tableColumns: [],
                tableTimer: null,
                tableFilter: {
                    searchText: "",
                    pageNo: 1,
                    pageSize: 100,
                    orderBy: this.isDailyStandup ? "incidentHandledDate" : "incidentStartDate",
                    sortDesc: this.isDailyStandup ? false : true,
                    totalItems: 0,
                }
            }
        },
        components: {
            LineChart,
            WordCloudChart,
            PieChart,
            ChartReplaceModal,
            TableChart
        },
        props: {
            chartId: {
                default: "",
                type: String
            },
            chartType: {
                default: "",
                type: String
            },
            reportLayoutId: {
                default: "",
                type: String
            },
            isReportBuilder: {
                default: false,
                type: Boolean
            },
            isDailyStandup: {
                default: false,
                type: Boolean
            },
            reportFilterOverride: {
                default: () => ({
                    startDate: null,
                    endDate: null,
                    name: "",
                    entity: "",
                    selectedOperatorBrands: []
                }),
                type: Object
            },
            showDateRange: {
                default: true,
                type: Boolean
            }
        },
        watch: {
            deep: true,
            immediate: true,
            reportFilterOverride: {
                handler() {
                    this.loadChart();
                },
                deep: true
            },
            "tableFilter.searchText": {
                handler() {
                    let self = this;
                    clearTimeout(this.tableTimer);
                    this.tableTimer = setTimeout(function () {
                        self.loadChart();
                    }, 1000);
                },
                deep: true
            },
            "tableFilter.pageNo": {
                handler() {
                    this.loadChart();
                },
                deep: true
            },
            "tableFilter.pageSize": {
                handler() {
                    this.loadChart();
                },
                deep: true
            },
            "tableFilter.orderBy": {
                handler() {
                    this.loadChart();
                },
                deep: true
            },
            "tableFilter.sortDesc": {
                handler() {
                    this.loadChart();
                },
                deep: true
            }
        },
        created() {
            this.setup();
        },
        methods: {
            async setup() {
                this.accessToken = this.$auth.getAccessToken();
                await this.getReportTableColumns();
                await this.loadChart();
            },
            async loadChart() {

                let chartRequest = {
                    reportDateType: this.reportFilterOverride.reportDateType,
                    startDate: moment.utc(`${this.reportFilterOverride.startDate} ${this.reportFilterOverride.startTime}`),
                    endDate: moment.utc(`${this.reportFilterOverride.endDate} ${this.reportFilterOverride.endTime}`),
                    chartFilterOverrides: this.getChartFilterOverrides(),
                    searchFilter: this.getSearchFilter()
                };

                switch (this.chartType) {
                    case "WORDCLOUD":
                        await this.loadWordcloudChart(chartRequest);
                        break;
                    case "PIE":
                        await this.loadPieChart(chartRequest);
                        break;
                    case "LINE":
                        await this.loadLineChart(chartRequest);
                        break;
                    case "TABLE":
                        await this.loadTableChart(chartRequest);
                        break;
                    default:
                }
            },
            camelCaseString(string) {
                return string.charAt(0).toLowerCase() + string.slice(1);
            },
            onClose() {
                this.isReplaceModalVisible = false;
            },
            onReplaced() {
                this.isReplaceModalVisible = false;
                this.$emit('replaced')
            },
            async getReportTableColumns() {
                this.isBusy = true;

                let result = await ReportTableService.searchReportTableColumns(
                    "",
                    "",
                    1,
                    100,
                    "ColumnName",
                    "ASCENDING",
                    this.accessToken);

                this.tableColumns = result.data;

                this.isBusy = false;
            },
            async loadWordcloudChart(chartRequest) {
                this.isChartLoading = true;
                this.chartResult = await reportService.getWordCloudReportByChartId(this.chartId, chartRequest, this.accessToken);
                this.isChartLoading = false;
            },
            async loadPieChart(chartRequest) {
                this.isChartLoading = true;
                this.chartResult = await reportService.getPieReportByChartId(this.chartId, chartRequest, this.accessToken);
                this.isChartLoading = false;
            },
            async loadLineChart(chartRequest) {
                this.isChartLoading = true;
                this.chartResult = await reportService.getLineReportByChartId(this.chartId, chartRequest, this.accessToken);
                this.isChartLoading = false;
            },
            async loadTableChart(chartRequest) {
                this.isChartLoading = true;
                this.tableFilter.totalItems = 0;
                this.chartResult.data = [];

                chartRequest.searchText = this.tableFilter.searchText;
                chartRequest.pageNo = this.tableFilter.pageNo;
                chartRequest.pageSize = this.tableFilter.pageSize;
                chartRequest.orderBy = this.tableFilter.orderBy;
                chartRequest.sorting = this.tableFilter.sortDesc === true ? 1 : 0;

                let results = await reportService.getTableReportByChartId(
                    this.chartId,
                    chartRequest,
                    this.accessToken);

                if (results && results.data.length > 0) {

                    const keys = Object.keys(results.data[0]);

                    this.chartHeaders = [];
                    keys.forEach((item) => {
                        let selectedColumn = this.tableColumns.find((tc) => tc.columnName.toLowerCase() === item.toLowerCase());
                        if (selectedColumn) {
                            this.chartHeaders.push({
                                text: selectedColumn.friendlyName,
                                align: 'start',
                                sortable: true,
                                cellClass: 'align-content-top',
                                value: this.camelCaseString(selectedColumn.columnName)
                            });
                        }
                    });

                    this.tableFilter.totalItems = results.count;
                    this.chartResult.data = results.data;
                }

                this.isChartLoading = false;
            },
            onOpenWordcloudCaption() {
                EventBus.$emit('emit-caption', 'wordcloud');
            },
            onOpenLinechartCaption() {
                EventBus.$emit('emit-caption', 'linechart');
            },
            onOpenTablechartCaption() {
                EventBus.$emit('emit-caption', 'table');
            },
            getSelectedSites() {

                let selectedSites = [];

                if (this.reportFilterOverride.selectedSites) {

                    this.reportFilterOverride.selectedSites.forEach(site => {
                        selectedSites.push(site);
                    })

                    //Work around for name changes
                    if (selectedSites.some(site => site === "MAL") && !selectedSites.some(site => site === "Malta")) {
                        selectedSites.push("Malta");
                    }

                    if (selectedSites.some(site => site === "GIC") && !selectedSites.some(site => site === "Gibraltar")) {
                        selectedSites.push("Gibraltar");
                    }

                    if (selectedSites.some(site => site === "CIL") && !selectedSites.some(site => site === "Croatia")) {
                        selectedSites.push("Croatia");
                    }
                }

                return selectedSites;
            },
            getChartFilterOverrides() {

                let filters = [];

                //operator
                if (this.reportFilterOverride.selectedOperatorBrands) {
                    filters.push({
                        "columnName": "AffectedOperator",
                        "columnFilters" : this.reportFilterOverride.selectedOperatorBrands
                    });
                }

                //incident types
                if (this.reportFilterOverride.selectedIncidentTypes) {
                    filters.push({
                        "columnName": "IncidentSeverity",
                        "columnFilters": this.reportFilterOverride.selectedIncidentTypes
                    });
                }

                //sites
                if (this.reportFilterOverride.selectedSites) {
                    filters.push({
                        "columnName": "AffectedSite",
                        "columnFilters": this.getSelectedSites()
                    });
                }

                //responsible teams
                if (this.reportFilterOverride.selectedResponsibleTeams) {
                    filters.push({
                        "columnName": "ResponsibleTeam",
                        "columnFilters": this.reportFilterOverride.selectedResponsibleTeams
                    });
                }

                return filters;
            },
            getSearchFilter() {

                if (this.reportFilterOverride.entity) {
                    return {
                        columnNames: ["Entity", "Issue"],
                        columnFilter: this.reportFilterOverride.entity
                    }
                } 

                if (!this.reportFilterOverride.name) return null;

                return {
                    columnNames: ["RootCause", "ResponsibleTeam"],
                    columnFilter: this.reportFilterOverride.name
                }
            },
            onOperatorSelect(evt) {
                this.$emit("on-operator-select", evt);
            },
            onSeveritySelect(evt) {
                this.$emit("on-severity-select", evt);
            }
        }
    }
</script>
